import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress, IconButton, Tooltip } from "@mui/joy";
import { NotificationsNoneIcon, NotificationsActiveIcon } from "../../styles/StyledMUI";
import type { AlertItem } from "../../api/zekay/alerts";
import { useBellAlerts, useBellAlertDisplay } from "../../contexts/app/bellAlertsContext";
import AlertsDrawer from "./AlertsDrawer";
import { colorTokens } from "../../styles/globalStyles";

type AlertsBellProps = {
  title?: string;
  buttonProps?: Partial<React.ComponentProps<typeof IconButton>>;
};

export default function AlertsBell({ title = "Alertes", buttonProps }: AlertsBellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getForPath } = useBellAlertDisplay();
  const { alerts, open, setOpen, isError, errorCount, resetErrorState, isLoading } =
    useBellAlerts();

  const pathname = location.pathname || "/";
  const ui = getForPath(pathname);
  const contextEnabled = ui.enabled;

  const visibleAlerts = React.useMemo(
    () => (alerts || []).filter((a) => a.category && (ui.categories || []).includes(a.category)),
    [alerts, ui.categories],
  );

  const drawerAlerts = React.useMemo(
    () => visibleAlerts.filter((a) => a.triggered === true),
    [visibleAlerts],
  );

  const hasTriggered = React.useMemo(() => drawerAlerts.length > 0, [drawerAlerts]);

  const handleAlertClick = React.useCallback(
    (a: AlertItem) => {
      setOpen(false);
      const target = a.target;
      if (!target) return;

      switch (target.kind) {
        case "hr":
          navigate("/hr", { state: { select: "Validation" } });
          return;
        case "equipment":
          navigate("/inventory", {
            state: {
              select: "Équipements",
              modelId: target.modelId,
              modelName: target.modelName,
              filters: target.filters,
              alertName: a.name,
            },
          });
          return;
        case "users":
          navigate("/users", {
            state: {
              select: "Utilisateurs",
              targetUserId: target.userId,
              openUserDocuments: true,
            },
          });
          return;
        case "trainings":
          navigate("/trainings", {
            state: {
              select: target.view === "cards" ? "Cartes" : "Formations",
              trainingTemplateId: target.trainingTemplateId,
              trainingStatus: target.statusCode,
              alertKey: target.alertKey,
              alertName: target.label,
            },
          });
          return;
        default:
          return;
      }
    },
    [navigate, setOpen],
  );

  if (!contextEnabled) return null;

  return (
    <>
      <Tooltip
        title={
          isLoading
            ? "Chargement des alertes..."
            : hasTriggered
              ? "Alertes actives"
              : "Aucune alerte"
        }
      >
        <IconButton
          onClick={() => {
            setOpen(true);
          }}
          {...buttonProps}
        >
          {isLoading ? (
            <CircularProgress size="sm" sx={{ color: colorTokens.accent.primary }} />
          ) : hasTriggered ? (
            <NotificationsActiveIcon sx={{ color: colorTokens.status.danger }} />
          ) : (
            <NotificationsNoneIcon sx={{ color: colorTokens.text.muted }} />
          )}
        </IconButton>
      </Tooltip>
      <AlertsDrawer
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        alerts={drawerAlerts}
        isError={isError}
        errorCount={errorCount}
        onResetError={resetErrorState}
        onAlertClick={handleAlertClick}
      />
    </>
  );
}
